import { ChevronRight, Plus, Radio } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { WorkspaceDrawerTrigger } from "../../components/layout/WorkspaceDrawerTrigger.js";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { MixedLocaleValue } from "../whatsapp/TechnicalValue.js";
import {
  ChannelDetailHeaderActions,
  channelDetailFallbackKey,
  channelDetailPlatform,
} from "./channel-detail-header.js";
import { useChannelDirectory } from "./ChannelsWorkspaceLayout.js";

const headerShellClass =
  "flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line/70 bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,252,0.94)_100%)] px-4 py-3.5";

export function ChannelsWorkspaceHeader(): React.JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const { openAddChannel, openMobileNav, mobileNavOpen, detailHeader, detailHeaderPending } =
    useChannelDirectory();
  const platform = channelDetailPlatform(location.pathname);
  const title = detailHeader?.title || (platform ? t(channelDetailFallbackKey(platform)) : "");

  return (
    <header className={cn(headerShellClass)} data-testid="channels-workspace-actions">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="inline-grid size-9 shrink-0 place-items-center rounded-xl border border-line/70 bg-surface text-accent shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
          <Radio size={18} aria-hidden="true" />
        </span>
        {platform ? (
          <div className="flex min-w-0 items-center gap-1 text-[0.7rem] text-muted">
            <Link
              to="/channels"
              className="shrink-0 text-muted no-underline transition-colors hover:text-foreground"
              data-testid="channels-detail-back"
            >
              {t("channels.home.title")}
            </Link>
            <ChevronRight
              size={12}
              className="shrink-0 opacity-60 rtl:-scale-x-100"
              aria-hidden="true"
            />
            <h1
              className="m-0 min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.02em] text-foreground"
              data-testid="channels-detail-title"
            >
              <MixedLocaleValue>{title}</MixedLocaleValue>
            </h1>
          </div>
        ) : (
          <div className="min-w-0">
            <h1 className="m-0 truncate text-[1rem] font-semibold tracking-[-0.01em] text-foreground">
              {t("channels.home.title")}
            </h1>
            <p className="m-0 text-[0.75rem] text-muted">{t("channels.home.description")}</p>
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <WorkspaceDrawerTrigger
          label={t("channels.nav.mobileMenu")}
          open={mobileNavOpen}
          onClick={openMobileNav}
          testId="channels-open-mobile-nav"
          ariaLabel={t("channels.nav.mobileMenu")}
        />
        {platform ? (
          <ChannelDetailHeaderActions
            pending={detailHeaderPending}
            disconnect={detailHeaderPending ? null : (detailHeader?.disconnect ?? null)}
          />
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={openAddChannel}
            data-testid="channels-add-button"
          >
            <Plus size={14} aria-hidden="true" />
            {t("channels.add.action")}
          </Button>
        )}
      </div>
    </header>
  );
}
